import React, { useState, useMemo } from 'react';
import {
  Box,
  Pagination,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  Avatar,
  Stack,
  Tabs,
  Tab,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Refresh,
  Search,
  FilterList,
  KeyboardArrowDown,
  KeyboardArrowUp,
  CreditCard,
  Edit,
  Delete,
  Add,
  Close,
  AttachMoney,
  AccountBalanceWallet,
  TrendingUp,
  Person,
  Email,
  Phone,
  Receipt,
  Payment,
  ShoppingCart,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListCreditUsersQuery,
  useListAllUsersQuery,
  useSetCreditMutation,
  useUpdateCreditMutation,
  useDeleteCreditMutation,
  useGetUserCreditDetailsQuery,
  CreditUser,
  User,
  SetCreditRequest,
  UpdateCreditRequest,
} from '../../features/admin/setCreditApi.ts';

interface CreditFormData {
  user_id: number | '';
  credit_limit: number | '';
  balance: number | '';
  credit_period_days: number | '';
}

interface CreditFormErrors {
  user_id?: string;
  credit_limit?: string;
  balance?: string;
  credit_period_days?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-component: mounts inside dialog, calls API with user_id, shows live data
// ─────────────────────────────────────────────────────────────────────────────
const TxnDialogContent: React.FC<{
  user: any;
  formatCurrency: (v: string | number) => string;
  formatDate: (s: string) => string;
  getOrderStatusColor: (s: any) => { bgcolor: string; color: string };
  getRepaymentStatusColor: (s: any) => { bgcolor: string; color: string };
}> = ({ user, formatCurrency, formatDate, getOrderStatusColor, getRepaymentStatusColor }) => {

  const { data, isLoading, isError, refetch } = useGetUserCreditDetailsQuery(
    { user_id: user.user_id },
    { refetchOnMountOrArgChange: true }
  );

  const details = data?.data || data?.result;

  const cl           = parseFloat(String(details?.credit_limit ?? user.credit?.credit_limit ?? user.credit_limit ?? 0));
  const bal          = parseFloat(String(details?.balance       ?? user.credit?.credit_balance ?? user.balance    ?? 0));
  const utilized     = cl - bal;
  const utilizationPct = cl > 0 ? Math.round((utilized / cl) * 100) : 0;
  const orders       = details?.orders     || [];
  const repayments   = details?.repayments || [];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress size={32} sx={{ color: '#e65100' }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" action={<Button size="small" onClick={() => refetch()}>Retry</Button>}>
          Failed to load credit details. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <>
      {/* Credit Summary */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 3 }}>
        <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5', bgcolor: '#fafbfb' }}>
          <Typography variant="caption" color="text.secondary">Credit Limit</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#7b1fa2', mt: 0.3 }}>{formatCurrency(cl)}</Typography>
        </Card>
        <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5', bgcolor: '#fafbfb' }}>
          <Typography variant="caption" color="text.secondary">Available Balance</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#00783e', mt: 0.3 }}>{formatCurrency(bal)}</Typography>
        </Card>
        <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #ffe0b2', bgcolor: '#fff8f0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">Utilized</Typography>
            <Chip
              label={`${utilizationPct}%`}
              size="small"
              sx={{
                bgcolor: utilizationPct >= 80 ? '#ffebee' : utilizationPct >= 50 ? '#fff3e0' : '#d1f4e0',
                color:   utilizationPct >= 80 ? '#c62828' : utilizationPct >= 50 ? '#e65100' : '#00783e',
                fontWeight: 700, fontSize: '10px', height: '20px',
              }}
            />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#e65100', mt: 0.3 }}>{formatCurrency(utilized)}</Typography>
        </Card>
      </Box>

      {/* Pay Later Orders */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <ShoppingCart sx={{ fontSize: 18, color: '#6d7175' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#202223' }}>Pay Later Orders</Typography>
          <Chip label={orders.length} size="small" sx={{ bgcolor: '#f1f2f4', color: '#6d7175', fontWeight: 600, height: '20px', fontSize: '11px' }} />
        </Box>

        {orders.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3, bgcolor: '#fafbfb', borderRadius: '10px', border: '1px dashed #e1e3e5' }}>
            <ShoppingCart sx={{ fontSize: 32, color: '#c9cccf', mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary">No pay later orders found</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '10px', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#fafbfb' }}>
                  {['Order ID', 'Invoice No', 'Amount', 'Status'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '11px', color: '#6d7175', textTransform: 'uppercase', py: 1.2 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order: any, idx: number) => (
                  <TableRow key={order.order_id || idx} sx={{ '&:hover': { bgcolor: '#fafbfb' }, '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell sx={{ py: 1.2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>#{order.order_id}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Typography variant="body2" color="text.secondary">{order.invoice_no || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{formatCurrency(order.total_order_amount || 0)}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Chip label={order.order_status || 'Unknown'} size="small"
                        sx={{ ...getOrderStatusColor(order.order_status), fontWeight: 600, fontSize: '11px', height: '22px', textTransform: 'capitalize' }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Repayment History */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Payment sx={{ fontSize: 18, color: '#6d7175' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#202223' }}>Repayment History</Typography>
          <Chip label={repayments.length} size="small" sx={{ bgcolor: '#f1f2f4', color: '#6d7175', fontWeight: 600, height: '20px', fontSize: '11px' }} />
        </Box>

        {repayments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3, bgcolor: '#fafbfb', borderRadius: '10px', border: '1px dashed #e1e3e5' }}>
            <Payment sx={{ fontSize: 32, color: '#c9cccf', mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary">No repayment history found</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '10px', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#fafbfb' }}>
                  {['Repayment ID', 'Order ID', 'Amount', 'Status', 'Date'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '11px', color: '#6d7175', textTransform: 'uppercase', py: 1.2 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {repayments.map((rep: any, idx: number) => (
                  <TableRow key={rep.repayment_id || idx} sx={{ '&:hover': { bgcolor: '#fafbfb' }, '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell sx={{ py: 1.2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>#{rep.repayment_id}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Typography variant="body2" color="text.secondary">{rep.order_id ? `#${rep.order_id}` : '—'}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#00783e' }}>{formatCurrency(rep.repayment_amount || rep.amount || 0)}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Chip label={rep.repayment_status || rep.status || 'Unknown'} size="small"
                        sx={{ ...getRepaymentStatusColor(rep.repayment_status || rep.status), fontWeight: 600, fontSize: '11px', height: '22px', textTransform: 'capitalize' }} />
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Typography variant="body2" color="text.secondary">{formatDate(rep.repayment_date)}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
const SetCredit = () => {
  const theme   = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen,       setMobileOpen]       = useState(false);
  const [page,             setPage]             = useState(1);
  const [searchStr,        setSearchStr]        = useState('');
  const [tabValue,         setTabValue]         = useState(0);
  const [snackbar,         setSnackbar]         = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [expandedUserId,   setExpandedUserId]   = useState<number | null>(null);
  const [formOpen,         setFormOpen]         = useState(false);
  const [editingCredit,    setEditingCredit]    = useState<CreditUser | null>(null);
  const [formData,         setFormData]         = useState<CreditFormData>({ user_id: '', credit_limit: '', balance: '', credit_period_days: 30 });
  const [formErrors,       setFormErrors]       = useState<CreditFormErrors>({});
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [deletingUserId,   setDeletingUserId]   = useState<number | null>(null);

  // Transaction dialog
  const [txnDialogOpen, setTxnDialogOpen] = useState(false);
  const [txnDialogUser, setTxnDialogUser] = useState<any>(null);

  // API hooks
  const { data: creditUsersResponse, isLoading, isError, refetch } = useListCreditUsersQuery();

  const { data: normalUsersResponse,   isLoading: isLoadingNormal,   refetch: refetchNormal   } = useListAllUsersQuery({ filter_type: 0 });
  const { data: silverUsersResponse,   isLoading: isLoadingSilver,   refetch: refetchSilver   } = useListAllUsersQuery({ filter_type: 1 });
  const { data: goldUsersResponse,     isLoading: isLoadingGold,     refetch: refetchGold     } = useListAllUsersQuery({ filter_type: 2 });
  const { data: platinumUsersResponse, isLoading: isLoadingPlatinum, refetch: refetchPlatinum } = useListAllUsersQuery({ filter_type: 3 });

  const isLoadingAllUsers = isLoadingNormal || isLoadingSilver || isLoadingGold || isLoadingPlatinum;
  const refetchAllUsers   = async () => { await Promise.all([refetchNormal(), refetchSilver(), refetchGold(), refetchPlatinum()]); };

  const [setCredit]                               = useSetCreditMutation();
  const [updateCredit]                            = useUpdateCreditMutation();
  const [deleteCredit, { isLoading: isDeleting }] = useDeleteCreditMutation();

  const creditUsers = creditUsersResponse?.data || creditUsersResponse?.result || [];

  const allUsers = useMemo(() => {
    const normal   = normalUsersResponse?.data   || normalUsersResponse?.result   || [];
    const silver   = silverUsersResponse?.data   || silverUsersResponse?.result   || [];
    const gold     = goldUsersResponse?.data     || goldUsersResponse?.result     || [];
    const platinum = platinumUsersResponse?.data || platinumUsersResponse?.result || [];
    return [...normal, ...silver, ...gold, ...platinum];
  }, [normalUsersResponse, silverUsersResponse, goldUsersResponse, platinumUsersResponse]);

  const usersPerPage = 10;
  const totalRecords = creditUsers.length;
  const totalPages   = Math.ceil(totalRecords / usersPerPage);

  // ── Helpers ──
  const getUserInitials = (fn?: string | null, ln?: string | null) => {
    const f = fn?.trim() || ''; const l = ln?.trim() || '';
    if (!f && !l) return '?';
    if (!f) return l[0].toUpperCase();
    if (!l) return f[0].toUpperCase();
    return `${f[0]}${l[0]}`.toUpperCase();
  };

  const getUserFullName = (fn?: string | null, ln?: string | null) => {
    const f = fn?.trim() || ''; const l = ln?.trim() || '';
    if (!f && !l) return 'Unknown User';
    if (!f) return l; if (!l) return f;
    return `${f} ${l}`;
  };

  const formatCurrency = (amount: string | number): string => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `AED ${(num || 0).toFixed(2)}`;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    try { return new Date(dateStr).toLocaleDateString('en-AE', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return dateStr; }
  };

  const calculateUtilization = (creditLimit: string | number, balance: string | number): number => {
    const limit = typeof creditLimit === 'string' ? parseFloat(creditLimit) : creditLimit;
    const bal   = typeof balance    === 'string' ? parseFloat(balance)    : balance;
    if (limit === 0) return 0;
    return Math.round(((limit - bal) / limit) * 100);
  };

  const getUtilizationColor = (p: number) =>
    p >= 80 ? { bgcolor: '#ffebee', color: '#c62828' } :
    p >= 50 ? { bgcolor: '#fff3e0', color: '#e65100' } :
              { bgcolor: '#d1f4e0', color: '#00783e' };

  const getOrderStatusColor = (status: any) => {
    const s = (String(status ?? '')).toLowerCase();
    if (s === 'completed' || s === 'delivered') return { bgcolor: '#d1f4e0', color: '#00783e' };
    if (s === 'pending')   return { bgcolor: '#fff3e0', color: '#e65100' };
    if (s === 'cancelled') return { bgcolor: '#ffebee', color: '#c62828' };
    return { bgcolor: '#f1f2f4', color: '#6d7175' };
  };

  const getRepaymentStatusColor = (status: any) => {
    const s = (String(status ?? '')).toLowerCase();
    if (s === 'paid' || s === 'completed' || s === 'success') return { bgcolor: '#d1f4e0', color: '#00783e' };
    if (s === 'pending') return { bgcolor: '#fff3e0', color: '#e65100' };
    if (s === 'failed')  return { bgcolor: '#ffebee', color: '#c62828' };
    return { bgcolor: '#f1f2f4', color: '#6d7175' };
  };

  const getUserEmail   = (user: any) => user.user_email || user.email || '';
  const getUserContact = (user: any) => user.user_contact || user.user_cortact || user.phone || '';
  const getCreditLimit = (user: any) => String(user.credit?.credit_limit || user.credit_list || user.credit_limit || '0');
  const getBalance     = (user: any) => String(user.credit?.credit_balance || user.balance || '0');

  const filteredUsers  = creditUsers.filter((user: any) => {
    if (!searchStr) return true;
    const fullName = getUserFullName(user.first_name, user.last_name);
    const email    = getUserEmail(user).toLowerCase();
    const contact  = getUserContact(user);
    const sl       = searchStr.toLowerCase();
    return fullName.toLowerCase().includes(sl) || email.includes(sl) || contact.includes(searchStr) || user.user_id.toString().includes(searchStr);
  });

  const paginatedUsers = filteredUsers.slice((page - 1) * usersPerPage, page * usersPerPage);

  const availableUsers = useMemo(() =>
    (!allUsers || allUsers.length === 0) ? [] :
    allUsers.filter((u: User) => !creditUsers.some((cu: any) => cu.user_id === u.user_id)),
  [allUsers, creditUsers]);

  // ── Handlers ──
  const handleDrawerToggle  = () => setMobileOpen(v => !v);
  const handleChangePage    = (_: React.ChangeEvent<unknown>, p: number) => setPage(p);
  const handleSearch        = (e: React.ChangeEvent<HTMLInputElement>) => { setSearchStr(e.target.value); setPage(1); };
  const handleCloseSnackbar = () => setSnackbar(s => ({ ...s, open: false }));
  const clearFilters        = () => { setSearchStr(''); setPage(1); };
  const handleToggleExpand  = (id: number) => setExpandedUserId(v => v === id ? null : id);

  const handleRefresh = async () => {
    try { await Promise.all([refetch(), refetchAllUsers()]); setSnackbar({ open: true, message: 'Refreshed!', severity: 'success' }); }
    catch { setSnackbar({ open: true, message: 'Error refreshing', severity: 'error' }); }
  };

  const resetForm       = () => { setFormData({ user_id: '', credit_limit: '', balance: '', credit_period_days: 30 }); setFormErrors({}); setEditingCredit(null); };
  const handleAddCredit = () => { resetForm(); setFormOpen(true); };
  const handleFormClose = () => { setFormOpen(false); resetForm(); };

  const handleEditCredit = (creditUser: any) => {
    setFormData({ user_id: creditUser.user_id, credit_limit: parseFloat(getCreditLimit(creditUser)) || 0, balance: parseFloat(getBalance(creditUser)) || 0, credit_period_days: Number(creditUser.credit_period_days) > 0 ? Number(creditUser.credit_period_days) : '' });
    setEditingCredit(creditUser);
    setFormOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let v: string | number = ['user_id', 'credit_limit', 'balance', 'credit_period_days'].includes(name) ? (value === '' ? '' : Number(value)) : value;
    setFormData(prev => ({ ...prev, [name]: v }));
    if (formErrors[name as keyof CreditFormErrors]) setFormErrors(prev => ({ ...prev, [name]: '' }));
    if (name === 'credit_limit' && !editingCredit && value !== '') setFormData(prev => ({ ...prev, balance: v }));
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: Number(value) }));
    if (formErrors[name as keyof CreditFormErrors]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = (): boolean => {
    const err: CreditFormErrors = {};
    if (!formData.user_id || formData.user_id === '') err.user_id = 'User selection is required';
    if (!formData.credit_limit || formData.credit_limit <= 0) err.credit_limit = 'Credit limit must be greater than 0';
    if (formData.balance === '' || formData.balance < 0) err.balance = 'Balance cannot be negative';
    if (formData.balance && formData.credit_limit && Number(formData.balance) > Number(formData.credit_limit)) err.balance = 'Balance cannot exceed credit limit';
    if (formData.credit_period_days !== '' && Number(formData.credit_period_days) < 1) err.credit_period_days = 'Credit period must be at least 1 day';
    if (!editingCredit && formData.credit_period_days === '') err.credit_period_days = 'Credit period is required';
    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsFormSubmitting(true);
    try {
      const payload = {
        user_id: Number(formData.user_id),
        credit_limit: Number(formData.credit_limit),
        balance: Number(formData.balance),
        ...(formData.credit_period_days !== '' ? { credit_period_days: Number(formData.credit_period_days) } : {}),
      };
      const result  = await (editingCredit ? updateCredit(payload) : setCredit(payload)).unwrap();
      setSnackbar({ open: true, message: result.message || `Credit ${editingCredit ? 'updated' : 'set'} successfully!`, severity: 'success' });
      handleFormClose();
      await Promise.all([refetch(), refetchAllUsers()]);
    } catch (error: any) {
      setSnackbar({ open: true, message: error?.data?.message || error.message || 'Error saving credit.', severity: 'error' });
    } finally { setIsFormSubmitting(false); }
  };

  const handleDelete = async (creditUser: any) => {
    if (window.confirm(`Remove credit facility for ${getUserFullName(creditUser.first_name, creditUser.last_name)}?`)) {
      try {
        setDeletingUserId(creditUser.user_id);
        const result = await deleteCredit({ user_id: creditUser.user_id }).unwrap();
        setSnackbar({ open: true, message: result.message || 'Removed!', severity: 'success' });
        await Promise.all([refetch(), refetchAllUsers()]);
      } catch (err: any) {
        setSnackbar({ open: true, message: err?.data?.message || err?.message || 'Error removing.', severity: 'error' });
      } finally { setDeletingUserId(null); }
    }
  };

  const handleOpenTxnDialog  = (e: React.MouseEvent, user: any) => { e.stopPropagation(); setTxnDialogUser(user); setTxnDialogOpen(true); };
  const handleCloseTxnDialog = () => { setTxnDialogOpen(false); setTxnDialogUser(null); };

  // Stats
  const totalBalance     = creditUsers.reduce((s: number, u: any) => s + (parseFloat(getBalance(u))     || 0), 0);
  const totalUtilized    = creditUsers.reduce((s: number, u: any) => s + ((parseFloat(getCreditLimit(u)) || 0) - (parseFloat(getBalance(u)) || 0)), 0);
  const totalCreditLimit = creditUsers.reduce((s: number, u: any) => s + (parseFloat(getCreditLimit(u)) || 0), 0);

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
        <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
      </Box>

      <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}>
        <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
      </Drawer>

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <Box sx={{ flexShrink: 0 }}><AdminHeader onToggleMenu={handleDrawerToggle} /></Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
          <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>

            {/* Page Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>Credit Sales Management</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh} disabled={isLoading}
                  sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { borderColor: '#202223', bgcolor: 'transparent' } }}>
                  Refresh
                </Button>
                <Button variant="contained" startIcon={<Add />} onClick={handleAddCredit}
                  sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { bgcolor: '#000000' } }}>
                  Add Credit
                </Button>
              </Box>
            </Box>

            {/* Stats */}
            {!isLoading && !isError && creditUsers.length > 0 && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { icon: <Person sx={{ color: '#1565c0', fontSize: 24 }} />, bg: '#e3f2fd', value: creditUsers.length, label: 'Credit Users', isCount: true },
                  { icon: <AccountBalanceWallet sx={{ color: '#7b1fa2', fontSize: 24 }} />, bg: '#f3e5f5', value: formatCurrency(totalCreditLimit), label: 'Total Credit Limit' },
                  { icon: <AttachMoney sx={{ color: '#2e7d32', fontSize: 24 }} />, bg: '#e8f5e9', value: formatCurrency(totalBalance), label: 'Available Balance' },
                  { icon: <TrendingUp sx={{ color: '#e65100', fontSize: 24 }} />, bg: '#fff3e0', value: formatCurrency(totalUtilized), label: 'Total Utilized' },
                ].map(({ icon, bg, value, label, isCount }) => (
                  <Grid item xs={12} sm={6} md={3} key={label}>
                    <Card sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', maxWidth: 320, width: '100%' }}>
                      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ bgcolor: bg, borderRadius: '8px', p: 1, display: 'flex' }}>{icon}</Box>
                        <Box>
                          <Typography variant={isCount ? 'h5' : 'h6'} sx={{ fontWeight: 600, color: '#202223', fontSize: isCount ? undefined : '18px' }}>{value}</Typography>
                          <Typography variant="body2" color="text.secondary">{label}</Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Table Card */}
            <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
              <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}
                  sx={{ px: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '14px', minHeight: 48, color: '#6d7175' }, '& .Mui-selected': { color: '#202223', fontWeight: 600 }, '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 3 } }}>
                  <Tab label={`All Users (${totalRecords})`} />
                </Tabs>
              </Box>

              <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                <TextField size="small" placeholder="Search by name, email, contact, or ID..." value={searchStr} onChange={handleSearch} fullWidth
                  sx={{ maxWidth: 400, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> }} />
                <Button variant="outlined" startIcon={<FilterList />} onClick={clearFilters}
                  sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { borderColor: '#202223', bgcolor: 'transparent' } }}>
                  Clear
                </Button>
              </Box>

              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                  <CircularProgress size={32} sx={{ color: '#008060' }} />
                </Box>
              )}
              {isError && !isLoading && (
                <Box sx={{ p: 3, bgcolor: 'white' }}><Alert severity="error">Failed to load credit users. Please try again.</Alert></Box>
              )}
              {!isLoading && !isError && paginatedUsers.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                  <CreditCard sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>No credit users found</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {searchStr ? `No results for "${searchStr}"` : 'Get started by adding credit facility for users'}
                  </Typography>
                  {!searchStr && (
                    <Button variant="contained" startIcon={<Add />} onClick={handleAddCredit}
                      sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500 }}>
                      Add First Credit Facility
                    </Button>
                  )}
                </Box>
              )}

              {!isLoading && paginatedUsers.length > 0 && (
                <Box sx={{ bgcolor: 'white', overflowX: 'auto' }}>
                  {!isMobile && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '48px 70px minmax(0, 1fr) 200px 140px 140px 100px 100px', minWidth: 1062, gap: 1.5, px: 2, py: 1.5, borderBottom: '1px solid #e1e3e5', bgcolor: '#fafbfb', alignItems: 'center' }}>
                      {['', 'ID', 'User', 'Contact', 'Credit Limit', 'Available', 'Utilized', 'Actions'].map(h => (
                        <Typography key={h} variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: h === 'Actions' ? 'center' : 'left' }}>{h}</Typography>
                      ))}
                    </Box>
                  )}

                  {paginatedUsers.map((user: any) => {
                    const userInitials   = getUserInitials(user.first_name, user.last_name);
                    const userFullName   = getUserFullName(user.first_name, user.last_name);
                    const userEmail      = getUserEmail(user);
                    const userContact    = getUserContact(user);
                    const creditLimit    = getCreditLimit(user);
                    const balance        = getBalance(user);
                    const utilization    = calculateUtilization(creditLimit, balance);
                    const isThisDeleting = deletingUserId === user.user_id;

                    return (
                      <Box key={user.user_id}>
                        <Box
                          sx={!isMobile ? {
                            display: 'grid', gridTemplateColumns: '48px 70px minmax(0, 1fr) 200px 140px 140px 100px 100px', minWidth: 1062,
                            gap: 1.5, px: 2, py: 2, alignItems: 'center', borderBottom: '1px solid #e1e3e5',
                            '&:hover': { bgcolor: '#f9fafb' }, transition: 'background-color 0.15s ease', cursor: 'pointer'
                          } : { p: 2, borderBottom: '1px solid #e1e3e5', '&:hover': { bgcolor: '#f9fafb' }, cursor: 'pointer' }}
                          onClick={() => handleToggleExpand(user.user_id)}
                        >
                          {!isMobile ? (
                            <>
                              <IconButton size="small" onClick={e => { e.stopPropagation(); handleToggleExpand(user.user_id); }} sx={{ color: '#6d7175', p: 0 }}>
                                {expandedUserId === user.user_id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                              </IconButton>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>#{user.user_id}</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: '#f1f2f4', color: '#202223', fontSize: '14px', fontWeight: 600 }}>{userInitials}</Avatar>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>{userFullName}</Typography>
                              </Box>
                              <Box>
                                {userEmail   && <Typography variant="body2"   color="text.secondary" sx={{ fontSize: '12px' }}>{userEmail}</Typography>}
                                {userContact && <Typography variant="caption" color="text.secondary">{userContact}</Typography>}
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#7b1fa2' }}>{formatCurrency(creditLimit)}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#00783e' }}>{formatCurrency(balance)}</Typography>
                              <Chip label={`${utilization}%`} size="small" sx={{ ...getUtilizationColor(utilization), fontWeight: 600, fontSize: '11px', height: '24px' }} />
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <IconButton onClick={e => { e.stopPropagation(); handleEditCredit(user); }} size="small" disabled={isThisDeleting} sx={{ color: '#202223' }}><Edit fontSize="small" /></IconButton>
                                <IconButton onClick={e => { e.stopPropagation(); handleDelete(user); }} disabled={isThisDeleting || isDeleting} size="small" sx={{ color: '#d32f2f' }}>
                                  {isThisDeleting ? <CircularProgress size={18} /> : <Delete fontSize="small" />}
                                </IconButton>
                              </Box>
                            </>
                          ) : (
                            <Stack spacing={2}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar sx={{ width: 40, height: 40, bgcolor: '#f1f2f4', color: '#202223', fontWeight: 600 }}>{userInitials}</Avatar>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{userFullName}</Typography>
                                    <Typography variant="caption" color="text.secondary">ID: {user.user_id}</Typography>
                                  </Box>
                                </Box>
                                <IconButton size="small" onClick={e => { e.stopPropagation(); handleToggleExpand(user.user_id); }} sx={{ color: '#6d7175' }}>
                                  {expandedUserId === user.user_id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#00783e' }}>{formatCurrency(balance)}</Typography>
                                  <Typography variant="caption" color="text.secondary">Available Balance</Typography>
                                </Box>
                                <Chip label={`${utilization}% Used`} size="small" sx={{ ...getUtilizationColor(utilization), fontWeight: 600 }} />
                              </Box>
                            </Stack>
                          )}
                        </Box>

                        {/* Expanded Details */}
                        <Collapse in={expandedUserId === user.user_id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202223' }}>Credit Details</Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button size="small" startIcon={<Edit />} onClick={() => handleEditCredit(user)} disabled={isThisDeleting} sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 500 }}>Edit</Button>
                                <Button size="small" color="error" startIcon={isThisDeleting ? <CircularProgress size={16} /> : <Delete />} onClick={() => handleDelete(user)} disabled={isThisDeleting || isDeleting} sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 500 }}>Remove</Button>
                              </Box>
                            </Box>

                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={4}>
                                <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white' }}>
                                  <Typography variant="caption" color="text.secondary">Credit Limit</Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#7b1fa2', mt: 0.5 }}>{formatCurrency(creditLimit)}</Typography>
                                </Card>
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white' }}>
                                  <Typography variant="caption" color="text.secondary">Available Balance</Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#00783e', mt: 0.5 }}>{formatCurrency(balance)}</Typography>
                                </Card>
                              </Grid>

                              {/* Utilized Amount — clickable card */}
                              <Grid item xs={12} sm={4}>
                                <Card
                                  onClick={e => handleOpenTxnDialog(e, user)}
                                  sx={{
                                    p: 2, borderRadius: '8px',
                                    border: '1px solid #ffe0b2', bgcolor: '#fff8f0',
                                    cursor: 'pointer',
                                    transition: 'all 0.18s ease',
                                    '&:hover': { bgcolor: '#fff3e0', borderColor: '#e65100', boxShadow: '0 2px 8px rgba(230,81,0,0.15)', transform: 'translateY(-1px)' },
                                  }}
                                >
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Typography variant="caption" color="text.secondary">Utilized Amount</Typography>
                                    <Receipt sx={{ fontSize: 16, color: '#e65100', opacity: 0.7 }} />
                                  </Box>
                                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#e65100', mt: 0.5 }}>
                                    {formatCurrency(parseFloat(creditLimit) - parseFloat(balance))}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#e65100', fontSize: '10px', mt: 0.3, display: 'block' }}>
                                    Click to view transactions ›
                                  </Typography>
                                </Card>
                              </Grid>
                            </Grid>

                            {(userEmail || userContact) && (
                              <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white', mt: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#202223' }}>Contact Information</Typography>
                                <Stack spacing={1.5}>
                                  {userEmail   && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Email   sx={{ color: '#6d7175', fontSize: 18 }} /><Typography variant="body2">{userEmail}</Typography></Box>}
                                  {userContact && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Phone   sx={{ color: '#6d7175', fontSize: 18 }} /><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{userContact}</Typography></Box>}
                                </Stack>
                              </Card>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Card>

            {totalPages > 1 && !isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination count={totalPages} page={page} onChange={handleChangePage}
                  sx={{ '& .MuiPaginationItem-root': { borderRadius: '8px', fontWeight: 500 }, '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' } }} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Add/Edit Credit Dialog */}
      <Dialog open={formOpen} onClose={handleFormClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{editingCredit ? 'Edit Credit Facility' : 'Add Credit Facility'}</Typography>
            <IconButton onClick={handleFormClose} size="small" disabled={isFormSubmitting}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={3}>
              <FormControl fullWidth disabled={isFormSubmitting || Boolean(editingCredit)} error={!!formErrors.user_id}>
                <InputLabel>Select User *</InputLabel>
                <Select name="user_id" value={formData.user_id.toString()} onChange={handleSelectChange} label="Select User *" disabled={Boolean(editingCredit)}
                  endAdornment={isLoadingAllUsers && !editingCredit ? <InputAdornment position="end" sx={{ mr: 2 }}><CircularProgress size={20} /></InputAdornment> : null}>
                  {editingCredit ? (
                    <MenuItem value={editingCredit.user_id}>{`${getUserFullName(editingCredit.first_name, editingCredit.last_name)} (ID: ${editingCredit.user_id})`}</MenuItem>
                  ) : availableUsers.length > 0 ? (
                    availableUsers.map((user: User) => (
                      <MenuItem key={user.user_id} value={user.user_id}>{`${getUserFullName(user.first_name, user.last_name)} (ID: ${user.user_id}) - ${user.user_email}`}</MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>{isLoadingAllUsers ? 'Loading users...' : 'No users available'}</MenuItem>
                  )}
                </Select>
                {formErrors.user_id && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{formErrors.user_id}</Typography>}
                {!editingCredit && availableUsers.length === 0 && !isLoadingAllUsers && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>All users already have credit facility assigned</Typography>
                )}
              </FormControl>

              <TextField name="credit_limit" label="Credit Limit *" type="number" value={formData.credit_limit} onChange={handleInputChange}
                error={!!formErrors.credit_limit} helperText={formErrors.credit_limit || 'Maximum credit amount allowed'}
                fullWidth required disabled={isFormSubmitting}
                InputProps={{ startAdornment: <InputAdornment position="start">AED</InputAdornment>, inputProps: { min: 0, step: 0.01 } }} />

              <TextField name="balance" label="Available Balance *" type="number" value={formData.balance} onChange={handleInputChange}
                error={!!formErrors.balance} helperText={formErrors.balance || 'Current available credit balance'}
                fullWidth required disabled={isFormSubmitting}
                InputProps={{ startAdornment: <InputAdornment position="start">AED</InputAdornment>, inputProps: { min: 0, step: 0.01 } }} />

              <TextField name="credit_period_days" label="Pay Later Credit Period *" type="number" value={formData.credit_period_days}
                onChange={handleInputChange} error={!!formErrors.credit_period_days}
                helperText={formErrors.credit_period_days || (editingCredit ? 'Enter days to change the period; leave blank to keep the current API value' : 'After this many days, unpaid Pay Later orders are auto-debited from the saved card')}
                fullWidth required disabled={isFormSubmitting}
                InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment>, inputProps: { min: 1, step: 1 } }} />

              {editingCredit && (
                <Alert severity="info" sx={{ borderRadius: '8px' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Current Status</Typography>
                  <Typography variant="body2">Credit Limit: {formatCurrency(getCreditLimit(editingCredit))}</Typography>
                  <Typography variant="body2">Available Balance: {formatCurrency(getBalance(editingCredit))}</Typography>
                  <Typography variant="body2">Utilized: {formatCurrency(parseFloat(getCreditLimit(editingCredit)) - parseFloat(getBalance(editingCredit)))}</Typography>
                  {formData.credit_period_days !== '' && <Typography variant="body2">Pay Later Period: {Number(formData.credit_period_days)} days</Typography>}
                </Alert>
              )}
              {!editingCredit && formData.credit_limit && (
                <Alert severity="info" sx={{ borderRadius: '8px' }}>
                  <Typography variant="body2">💡 Balance is typically set equal to credit limit initially.</Typography>
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
             <Button onClick={handleFormClose} disabled={isFormSubmitting} variant="outlined" sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#f44336', color: '#eeeeee', fontWeight: 500 }}>Close</Button>
            <Button onClick={handleFormClose} disabled={isFormSubmitting} variant="outlined" sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#F78E12', color: '#eeeeee', fontWeight: 500 }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={isFormSubmitting || !formData.user_id || !formData.credit_limit || formData.balance === '' || (!editingCredit && formData.credit_period_days === '')}
              sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { bgcolor: '#000000' } }}>
              {isFormSubmitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  {editingCredit ? 'Updating...' : 'Setting...'}
                </Box>
              ) : (editingCredit ? 'Update Credit' : 'Set Credit')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Transaction Dialog — TxnDialogContent fetches live data per user_id */}
      <Dialog open={txnDialogOpen} onClose={handleCloseTxnDialog} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px', maxHeight: '90vh' } }}>
        <DialogTitle sx={{ pb: 0, pt: 2.5, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ bgcolor: '#fff3e0', borderRadius: '8px', p: 0.8, display: 'flex' }}>
                <Receipt sx={{ color: '#e65100', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#202223', lineHeight: 1.2 }}>Credit Transactions</Typography>
                {txnDialogUser && (
                  <Typography variant="caption" color="text.secondary">
                    {getUserFullName(txnDialogUser.first_name, txnDialogUser.last_name)} &nbsp;·&nbsp; ID #{txnDialogUser.user_id}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton onClick={handleCloseTxnDialog} size="small"><Close /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
          {txnDialogUser && (
            <TxnDialogContent
              user={txnDialogUser}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              getOrderStatusColor={getOrderStatusColor}
              getRepaymentStatusColor={getRepaymentStatusColor}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseTxnDialog} variant="contained"
            sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { bgcolor: '#000' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SetCredit;